import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCalendarDay,
  faCheck,
  faShirt,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Whistle } from '@sorare/core/src/atoms/icons/Whistle';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { groupBy, sortBy, uniqueBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';

import { FixtureTimelineGame } from './FixtureTimelineGame';
import { MainItemContent } from './MainItemContent';
import type {
  FixtureTimeline_composeTeamBenchObjectInterface,
  FixtureTimeline_game,
  FixtureTimeline_so5Fixture,
  FixtureTimeline_so5Lineup,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  isolation: isolate;
  --dot-width: calc(var(--quadruple-unit));
`;

const TimelineRowWrapper = styled(Horizontal)`
  border-radius: var(--unit);
  align-items: flex-start;
  position: relative;
`;
const Dot = styled(Horizontal).attrs({ gap: 0, center: true })<{
  completed?: boolean;
}>`
  width: var(--dot-width);
  height: var(--dot-width);
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 1;
  ${({ completed }) =>
    completed
      ? css`
          background: var(--progress-color);
          color: var(--c-black);
        `
      : css`
          background-color: var(--c-nd-100-opaque);
          color: var(--white);
        `}
`;

const VerticalLine = styled.div`
  --line-width: 10px;
  width: var(--line-width);
  background: linear-gradient(
    to bottom,
    var(--progress-color) var(--progress),
    var(--c-nd-150) var(--progress),
    var(--c-nd-150) 100%
  );
  min-height: var(--quadruple-unit);
  margin-bottom: calc(-1 * var(--intermediate-unit));
  position: absolute;
  top: calc(var(--dot-width) - 1px);
  left: calc(var(--dot-width) / 2 - var(--line-width) / 2);
  z-index: -1;
  bottom: 0;
`;

const AppearanceImg = styled.img`
  width: var(--quadruple-unit);
`;

const RowContent = styled(Vertical)`
  padding: var(--half-unit) 0 var(--quadruple-unit) 0;
  flex: 1;
  &.isLastRow {
    padding-bottom: 0;
  }
`;

const Cards = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Date = styled(BodyS)`
  font-variant-numeric: tabular-nums;

  &.hide {
    visibility: hidden;
  }
`;

type TimelineRowProps = {
  label?: ReactNode;
  date?: Date;
  progress: number;
  isLastRow?: boolean;
  dot?: (
    Dot: ComponentType<PropsWithChildren<{ completed?: boolean }>>
  ) => ReactNode;
  icon?: ReactNode;
};

const TimelineRow = ({
  label,
  progress,
  children,
  date,
  isLastRow,
  dot,
  icon,
}: PropsWithChildren<TimelineRowProps>) => {
  return (
    <TimelineRowWrapper>
      {!isLastRow && (
        <VerticalLine
          style={
            {
              '--progress': `${progress * 100}%`,
            } as CSSProperties
          }
        />
      )}
      {dot?.(Dot) || <Dot completed={progress > 0}>{icon}</Dot>}
      <RowContent className={classNames({ isLastRow })}>
        <LabelL bold color={progress > 0 ? 'var(--c-white)' : 'currentColor'}>
          {label || (
            <FormattedDate
              value={date}
              day="numeric"
              month="short"
              weekday="long"
            />
          )}
        </LabelL>
        <div>{children}</div>
      </RowContent>
    </TimelineRowWrapper>
  );
};

type Props = {
  so5Fixture: FixtureTimeline_so5Fixture;
  so5Lineup?: FixtureTimeline_so5Lineup | null;
  // read next games info from bench objects if available
  // useful for the case where lineup is not saved yet
  benchObjects?: FixtureTimeline_composeTeamBenchObjectInterface[];
  onClickGame?: (gameId: string) => void;
  rewardsTitle?: ReactNode;
};

export const FixtureTimeline = ({
  so5Fixture,
  so5Lineup,
  benchObjects,
  onClickGame,
  rewardsTitle,
}: Props) => {
  const { formatDate } = useIntl();

  const benchObjectGames =
    benchObjects?.flatMap(
      benchObject => benchObject.anyPlayer?.anyGamesForLeaderboard
    ) || [];

  const games = sortBy(
    game => game?.date || '',
    uniqueBy(
      game => game?.id || '',
      benchObjectGames.length > 0
        ? benchObjectGames
        : so5Lineup?.so5Appearances
            .flatMap(appearance => appearance.eligiblePlayerGameScores)
            .map(gameScore => gameScore.anyGame) || []
    )
  ).filter(Boolean);

  const gamesByDate = groupBy(games, game =>
    formatDate(game.date, { format: 'yyyy-MM-dd' })
  );
  const lineupSubmitted = !!so5Lineup && !so5Lineup.draft;

  return (
    <Wrapper
      gap={3}
      style={{ '--progress-color': 'var(--c-score-high)' } as CSSProperties}
    >
      <Vertical gap={0}>
        <TimelineRow
          label={
            lineupSubmitted ? (
              <FormattedMessage
                id="FixtureTimeline.lineupIsSet"
                defaultMessage="Lineup is set"
              />
            ) : (
              <FormattedMessage
                id="FixtureTimeline.setYourLineup"
                defaultMessage="Set your lineup"
              />
            )
          }
          progress={lineupSubmitted ? 1 : 0}
          dot={D => (
            <D completed={lineupSubmitted}>
              {lineupSubmitted ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                <FontAwesomeIcon icon={faShirt} />
              )}
            </D>
          )}
        />
        {!lineupSubmitted && (
          // display gameweek starts if lineup not set to avoid an empty timeline
          <TimelineRow
            date={so5Fixture.startDate}
            icon={<Whistle />}
            progress={isPast(so5Fixture.startDate) ? 1 : 0}
          >
            <Horizontal>
              <Date color="var(--c-nd-700)">
                <FormattedDate
                  value={so5Fixture.startDate}
                  hour="2-digit"
                  minute="2-digit"
                />
              </Date>
              <MainItemContent>
                <BodyM>
                  <FormattedMessage
                    id="FixtureStatus.gameweekStarts"
                    defaultMessage="Gameweek starts"
                  />
                </BodyM>
              </MainItemContent>
            </Horizontal>
          </TimelineRow>
        )}
        {Object.entries(gamesByDate).map(([date, gamesList]) => (
          <TimelineRow
            key={date}
            date={gamesList?.at(0)?.date}
            icon={<FontAwesomeIcon icon={faCalendarDay} />}
            progress={
              (gamesList?.reduce(
                (acc, cur) => (isPast(cur.date) ? acc + 1 : acc),
                0
              ) || 0) / (gamesList?.length || 1)
            }
          >
            <Vertical gap={2}>
              {gamesList?.map(game => {
                const correspondingBenchObjects = benchObjects?.length
                  ? benchObjects?.filter(benchObject =>
                      benchObject.anyPlayer?.anyGamesForLeaderboard.some(
                        benchObjectGame => benchObjectGame?.id === game.id
                      )
                    )
                  : so5Lineup?.so5Appearances.filter(appearance =>
                      appearance.eligiblePlayerGameScores.some(
                        gameScore => gameScore.anyGame?.id === game.id
                      )
                    );
                return (
                  <SBHorizontal key={game.id} gap={8}>
                    <ConditionalWrapper
                      wrap={!!onClickGame}
                      Wrapper={ButtonBase}
                      props={{ onClick: () => onClickGame?.(game.id) }}
                    >
                      <FixtureTimelineGame game={game} />
                    </ConditionalWrapper>
                    <Cards>
                      {correspondingBenchObjects?.map(appearance => (
                        <AppearanceImg
                          src={appearance.pictureUrl ?? undefined}
                          alt=""
                          key={appearance.id}
                        />
                      ))}
                    </Cards>
                  </SBHorizontal>
                );
              })}
            </Vertical>
          </TimelineRow>
        ))}
        <TimelineRow
          date={so5Fixture.endDate}
          isLastRow
          icon={<Box />}
          progress={
            isPast(
              so5Fixture.expectedRewardsDistributionDate || so5Fixture.endDate
            )
              ? 1
              : 0
          }
        >
          <Horizontal>
            <Date
              color="var(--c-nd-700)"
              className={classNames({
                hide: !so5Fixture.expectedRewardsDistributionDate,
              })}
            >
              <FormattedDate
                value={
                  so5Fixture.expectedRewardsDistributionDate ||
                  so5Fixture.endDate
                }
                hour="2-digit"
                minute="2-digit"
              />
            </Date>
            <MainItemContent>
              <BodyM>
                {rewardsTitle || <FormattedMessage {...glossary.rewards} />}
              </BodyM>
            </MainItemContent>
          </Horizontal>
        </TimelineRow>
      </Vertical>
    </Wrapper>
  );
};

const gameFragment = gql`
  fragment FixtureTimeline_game on AnyGameInterface {
    id
    date
    homeTeam {
      slug
      code
    }
    awayTeam {
      slug
      code
    }
    ...FixtureTimelineGame_game
  }
  ${FixtureTimelineGame.fragments.game}
` as TypedDocumentNode<FixtureTimeline_game>;

FixtureTimeline.fragments = {
  so5Fixture: gql`
    fragment FixtureTimeline_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
      expectedRewardsDistributionDate
    }
  ` as TypedDocumentNode<FixtureTimeline_so5Fixture>,
  composeTeamBenchObjectInterface: gql`
    fragment FixtureTimeline_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      pictureUrl
      anyPlayer {
        slug
        anyGamesForLeaderboard(so5LeaderboardSlug: $so5LeaderboardSlug) {
          id
          ...FixtureTimeline_game
        }
      }
    }
    ${gameFragment}
  ` as TypedDocumentNode<FixtureTimeline_composeTeamBenchObjectInterface>,
  so5Lineup: gql`
    fragment FixtureTimeline_so5Lineup on So5Lineup {
      id
      draft
      so5Appearances {
        id
        pictureUrl
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            ...FixtureTimeline_game
          }
        }
      }
    }
    ${gameFragment}
  ` as TypedDocumentNode<FixtureTimeline_so5Lineup>,
};
